import { useState, useEffect, useRef } from "react";
import { X, MagnifyingGlass, User, DeviceMobile, CurrencyCircleDollar, Camera, Plus, Trash, CheckCircle, Printer, IdentificationCard, MapPin, Phone, CaretRight, UserPlus } from "@phosphor-icons/react";
import { clampPct } from "./TradeInDetailDrawer";
import { api, formatApiError, TRADE_IN_PAYOUT_LABELS } from "../lib/api";
import { TradeInDeviceFields } from "./TradeInDeviceFields";
import { toast } from "sonner";
import { isValidAuPhone, formatAuPhone, cleanPhone } from "../lib/auPhone";
import AddressAutocomplete from "./AddressAutocomplete";

/**
 * Trade-In Intake Modal — captures everything needed to BUY a used device.
 *
 * Design notes (post-feedback):
 *   - **Unified customer picker**: a single big search bar accepts BOTH
 *     phone numbers and names; matches show as a live dropdown beneath.
 *     The first row of the dropdown is always "+ Add new customer" so the
 *     cashier can create one without leaving the search flow. This
 *     replaces the older "search → no-match → click create-new" two-step.
 *   - **AU phone validation**: live + on-blur. The Save button on the
 *     create-new sub-form is disabled until the phone passes the
 *     `isValidAuPhone()` check. Keeps junk records out of the customer
 *     table.
 *   - **Address autocomplete**: powered by Nominatim (free, no key). Big,
 *     clean input that surfaces street/suburb/state/postcode hits as the
 *     cashier types. Selecting a hit fills the field with a normalised,
 *     compliance-friendly address string.
 *   - **Intake receipt PDF**: on success the modal exposes a "Print
 *     receipt" button that opens `/api/trade-ins/{id}/receipt/pdf` in a
 *     new tab so the cashier can hand a copy to the customer.
 */
const STATES_AU = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const dataUrlSize = (s) => Math.ceil(((s || "").length - (s.indexOf(",") + 1)) * 0.75);

export default function TradeInIntakeModal({ onClose, onCreated }) {
  // ── Customer state ─────────────────────────────────────────────────
  const [customerSearch, setCustomerSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [customerEdits, setCustomerEdits] = useState({});
  const [creatingNew, setCreatingNew] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "", phone: "", email: "",
    licence_number: "", licence_state: "QLD", address: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const blurTimer = useRef(null);

  // ── Devices + shared payout ────────────────────────────────────────
  // A single intake can capture N devices from the same customer (Rick
  // walks in with 5 phones). Each row has its own make/model/imei/
  // condition/notes/buy_price/sell_price; the customer picker + payout
  // method + licence photo are SHARED across the batch. On submit we
  // hit /api/trade-ins/batch which returns N TradeIn records, each with
  // a unique trade_in_number, all linked by a shared batch_id.
  const emptyDevice = () => ({
    device_make: "", device_model: "", device_type: "", storage: "", imei: "", battery_health: "", condition: "Good",
    notes: "", buy_price: "", sell_price: "",
  });
  const [devices, setDevices] = useState([emptyDevice()]);
  const [payoutMethod, setPayoutMethod] = useState("cash");
  const [payoutReference, setPayoutReference] = useState("");
  const [licencePhotoUrl, setLicencePhotoUrl] = useState("");

  const updateDevice = (idx, patch) =>
    setDevices((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  const removeDevice = (idx) => {
    if (devices.length === 1) return;
    setDevices((prev) => prev.filter((_, i) => i !== idx));
  };
  const addDevice = () => {
    if (devices.length >= 25) { toast.error("Maximum 25 devices per intake"); return; }
    setDevices((prev) => [...prev, emptyDevice()]);
  };

  const totalPayout = devices.reduce((s, d) => s + (parseFloat(d.buy_price) || 0), 0);
  const totalResale = devices.reduce((s, d) => s + (parseFloat(d.sell_price) || 0), 0);

  const [submitting, setSubmitting] = useState(false);
  const [createdBatch, setCreatedBatch] = useState(null);

  // ── Live customer search ───────────────────────────────────────────
  // Returns up to 6 matches by phone OR name. The dropdown renders these
  // plus a permanent "+ Add new" row at the bottom — single-step picker.
  useEffect(() => {
    const v = customerSearch.trim();
    if (v.length < 2) { setMatches([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/customers", { params: { q: v, limit: 6 } });
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setMatches(list);
        setSearchHighlight(0);
      } catch {
        if (!cancelled) setMatches([]);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [customerSearch]);

  const pickCustomer = (c) => {
    setCustomer(c);
    setCustomerEdits({
      licence_number: c.licence_number || "",
      licence_state: c.licence_state || "QLD",
      address: c.address || "",
    });
    setSearchOpen(false);
    setCustomerSearch(c.name + " · " + (c.phone || ""));
    setCreatingNew(false);
  };

  const startNew = () => {
    setCreatingNew(true);
    setCustomer(null);
    setSearchOpen(false);
    // Pre-fill phone from search box if it looks numeric.
    const looksLikePhone = /^[\d\s\-+()]+$/.test(customerSearch.trim());
    setNewCustomer((s) => ({
      ...s,
      phone: looksLikePhone ? customerSearch.trim() : "",
      name: !looksLikePhone ? customerSearch.trim() : "",
    }));
  };

  const validatePhoneOnBlur = (phone) => {
    if (!phone) { setPhoneError(""); return; }
    setPhoneError(isValidAuPhone(phone)
      ? ""
      : "Enter a valid Australian number (e.g. 0400 000 000 or +61 4XX XXX XXX)");
  };

  const saveCustomerEdits = async () => {
    if (!customer) return null;
    const needsUpdate =
      (customerEdits.licence_number || "") !== (customer.licence_number || "") ||
      (customerEdits.licence_state || "") !== (customer.licence_state || "") ||
      (customerEdits.address || "") !== (customer.address || "");
    if (!needsUpdate) return customer;
    try {
      const payload = {
        name: customer.name, phone: customer.phone, email: customer.email,
        notes: customer.notes || "", company: customer.company || "",
        abn: customer.abn || "",
        licence_number: customerEdits.licence_number || "",
        licence_state: customerEdits.licence_state || "",
        address: customerEdits.address || "",
      };
      const r = await api.put(`/customers/${customer.id}`, payload);
      setCustomer(r.data);
      return r.data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createNewCustomer = async () => {
    const errs = [];
    if (!newCustomer.name.trim()) errs.push("Customer name");
    if (!newCustomer.phone.trim()) errs.push("Phone");
    if (newCustomer.phone && !isValidAuPhone(newCustomer.phone)) errs.push("Valid Australian phone");
    if (!newCustomer.licence_number.trim()) errs.push("Licence number");
    if (!newCustomer.address.trim()) errs.push("Address");
    if (errs.length) { toast.error("Required: " + errs.join(", ")); return null; }

    try {
      const r = await api.post("/customers", newCustomer);
      const c = r.data;
      pickCustomer(c);
      toast.success(`Customer ${c.name} created`);
      return c;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const onPhotoFile = (file) => {
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Photo too large (max 1MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setLicencePhotoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!customer) { toast.error("Pick or create a customer first"); return; }
    // Validate every device row.
    for (let i = 0; i < devices.length; i++) {
      const d = devices[i];
      if (!d.device_make.trim() || !d.device_model.trim()) {
        toast.error(`Device #${i + 1}: make and model required`); return;
      }
      const bp = parseFloat(d.buy_price);
      const sp = parseFloat(d.sell_price);
      if (!(bp >= 0)) { toast.error(`Device #${i + 1}: buy price required`); return; }
      if (!(sp >= 0)) { toast.error(`Device #${i + 1}: sell price required`); return; }
    }
    const needsRef = payoutMethod === "bank_transfer" || payoutMethod === "payid";
    if (needsRef && !payoutReference.trim()) {
      toast.error(`Enter the ${TRADE_IN_PAYOUT_LABELS[payoutMethod]} reference`); return;
    }

    setSubmitting(true);
    try {
      const cust = await saveCustomerEdits();
      if (!cust) { setSubmitting(false); return; }
      if (!cust.licence_number?.trim() || !cust.address?.trim()) {
        toast.error("Customer must have licence number AND address before a trade-in");
        setSubmitting(false); return;
      }
      // Always use the batch endpoint — it handles N=1 as well as N>1
      // and gives us a single response shape either way.
      const r = await api.post("/trade-ins/batch", {
        customer_id: cust.id,
        payout_method: payoutMethod,
        payout_reference: needsRef ? payoutReference.trim() : "",
        licence_photo_url: licencePhotoUrl || "",
        devices: devices.map((d) => ({
          device_make: d.device_make.trim(),
          device_model: d.device_model.trim(),
          device_type: (d.device_type || "").trim(),
          storage: (d.storage || "").trim(),
          imei: d.imei.trim(),
          battery_health: (d.battery_health || "").trim(),
          condition: d.condition.trim(),
          notes: d.notes.trim(),
          buy_price: parseFloat(d.buy_price) || 0,
          sell_price: parseFloat(d.sell_price) || 0,
        })),
      });
      setCreatedBatch(r.data);
      const n = r.data.batch_size;
      toast.success(
        `${n} device${n === 1 ? "" : "s"} intaken · ` +
        (payoutMethod === "cash"
          ? `$${r.data.total_buy.toFixed(2)} from till`
          : payoutMethod === "store_credit"
            ? `$${r.data.total_buy.toFixed(2)} store credit`
            : `$${r.data.total_buy.toFixed(2)} by ${TRADE_IN_PAYOUT_LABELS[payoutMethod]}`)
      );
      // ── Auto-print device labels (respects user/store prefs) ──
      try {
        const prefs = (await api.get("/users/me/print-prefs")).data;
        if (prefs?.auto_print_label && prefs?.label_printer_id) {
          for (const ti of r.data.trade_ins) {
            await api.post(`/print/trade-in-label/${ti.id}`, {
              printer_id: prefs.label_printer_id,
              copies: 1,
            });
          }
          toast.success(`${n} label${n === 1 ? "" : "s"} sent to printer`);
        }
      } catch (e) {
        console.warn("[trade-in] auto-print label failed:", e);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // ── SUCCESS state (works for 1 or N devices) ───────────────────────
  if (createdBatch) {
    const n = createdBatch.batch_size;
    const openReceipt = async (tiId) => {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/trade-ins/${tiId}/receipt/pdf`;
      const tok = localStorage.getItem("pos_token");
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
        const blob = await r.blob();
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
      } catch {
        toast.error("Couldn't fetch the intake receipt");
      }
    };
    const openLabel = async (tiId) => {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/trade-ins/${tiId}/label/pdf`;
      const tok = localStorage.getItem("pos_token");
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
        const blob = await r.blob();
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
      } catch {
        toast.error("Couldn't fetch the device label");
      }
    };
    return (
      <Wrapper onClose={() => onCreated?.(createdBatch)}>
        <div className="p-8" data-testid="ti-intake-success">
          <div className="text-center">
            <CheckCircle size={56} weight="fill" className="mx-auto text-[#1F6B4F] mb-3" />
            <div className="font-display font-semibold text-[20px]">
              {n === 1 ? "Trade-in saved" : `${n} devices intaken`}
            </div>
            <div className="mt-2 mb-5 text-[#6B7280]">
              {n === 1
                ? "Hand the customer a printed receipt and stick the device label on the back:"
                : "One customer, multiple devices. Print each receipt / label as needed:"}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-left mb-5">
            <Stat label="Devices" value={String(n)} testid="ti-success-count" />
            <Stat label="Total payout" value={`$${createdBatch.total_buy.toFixed(2)}`} testid="ti-success-total-payout" />
            <Stat label="Method" value={TRADE_IN_PAYOUT_LABELS[payoutMethod] || "Cash"} />
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {createdBatch.trade_ins.map((ti, i) => (
              <div
                key={ti.id}
                className="flex items-center gap-3 border border-[#E5E7EB] rounded-lg px-3 py-2 bg-white"
                data-testid={`ti-success-row-${ti.trade_in_number}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#1E5FEB] flex items-center justify-center shrink-0 font-mono text-[11px] font-bold">
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">
                    {ti.device_make} {ti.device_model}{ti.storage ? ` ${ti.storage}` : ""}
                  </div>
                  <div className="text-[11px] text-[#6B7280] font-mono truncate">
                    <span className="text-[#1849C8] font-semibold">{ti.trade_in_number}</span>
                    {ti.imei ? ` · IMEI ${ti.imei}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0 mr-2">
                  <div className="text-[13px] font-mono font-semibold text-emerald-700">
                    ${Number(ti.buy_price).toFixed(2)}
                  </div>
                </div>
                <button
                  onClick={() => openReceipt(ti.id)}
                  className="h-8 px-2 rounded border border-[#E5E7EB] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] hover:border-[#121216] hover:text-[#121216] inline-flex items-center gap-1"
                  data-testid={`ti-success-receipt-${ti.trade_in_number}`}
                  title="Intake receipt PDF"
                >
                  <Printer size={11} weight="bold" />
                  Receipt
                </button>
                <button
                  onClick={() => openLabel(ti.id)}
                  className="h-8 px-2 rounded border border-[#BFDBFE] text-[10px] font-semibold uppercase tracking-wider text-[#1E5FEB] hover:bg-[#EFF6FF] inline-flex items-center gap-1"
                  data-testid={`ti-success-label-${ti.trade_in_number}`}
                  title="Dymo device label"
                >
                  <Printer size={11} weight="bold" />
                  Label
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-end">
            <button
              onClick={() => onCreated?.(createdBatch)}
              className="h-10 px-5 rounded-lg bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8]"
              data-testid="ti-intake-success-done"
            >
              Done
            </button>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper onClose={onClose}>
      <Header title="Buy Device (Trade-In)" subtitle="Add one or many devices in one intake." onClose={onClose} />

      <div className="p-6 space-y-6 max-h-[calc(100vh-180px)] overflow-y-auto">
        {/* ── 1. CUSTOMER (unified picker) ────────────────────────── */}
        <Section icon={<User size={16} weight="bold" />} title="Customer" subtitle="Search by name or phone, or add new. Licence + address required for second-hand goods.">
          {!creatingNew && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setSearchOpen(true); if (customer) setCustomer(null); }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => { blurTimer.current = setTimeout(() => setSearchOpen(false), 150); }}
                  onKeyDown={(e) => {
                    if (!searchOpen) return;
                    const total = matches.length + 1;   // +1 for the "Add new" row in the dropdown
                    if (e.key === "ArrowDown") { e.preventDefault(); setSearchHighlight((h) => Math.min(h + 1, total - 1)); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); setSearchHighlight((h) => Math.max(h - 1, 0)); }
                    else if (e.key === "Enter") {
                      e.preventDefault();
                      if (searchHighlight === matches.length) startNew();
                      else if (matches[searchHighlight]) pickCustomer(matches[searchHighlight]);
                    }
                    else if (e.key === "Escape") setSearchOpen(false);
                  }}
                  placeholder="Search by name or phone…"
                  className="h-14 w-full pl-12 pr-4 rounded-2xl border-2 border-[#E5E7EB] text-[15px] focus:border-[#1E5FEB] outline-none transition-colors"
                  data-testid="ti-intake-customer-search"
                  autoFocus
                />
              </div>
              {/* Explicit Add button — always visible so the cashier never
                  has to rely on the dropdown's "Add new" row. Disabled when
                  a customer is already selected to avoid accidental blanks. */}
              <button
                type="button"
                onClick={() => { startNew(); }}
                disabled={!!customer}
                className="h-14 px-5 rounded-2xl bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 flex-shrink-0"
                data-testid="ti-intake-customer-add-btn"
              >
                <UserPlus size={15} weight="bold" />
                Add
              </button>
            </div>
          )}
          {!creatingNew && searchOpen && customerSearch.trim().length >= 2 && (
            <div
              className="relative"
              data-testid="ti-intake-customer-dropdown-wrapper"
            >
              <div
                className="absolute left-0 right-0 top-1 mt-2 bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden z-30"
                style={{ boxShadow: "0 14px 38px rgba(30, 95, 235, 0.15)" }}
                onMouseDown={() => clearTimeout(blurTimer.current)}
                data-testid="ti-intake-customer-dropdown"
              >
                {matches.length === 0 && (
                  <div className="px-4 py-3 text-[12.5px] text-[#9CA3AF]">No matches — use the <span className="font-semibold text-[#1E5FEB]">Add</span> button.</div>
                )}
                {matches.map((c, i) => {
                  const hi = i === searchHighlight;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onMouseEnter={() => setSearchHighlight(i)}
                      onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                      className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 ${hi ? "bg-[#F3F4F6]" : "hover:bg-[#FAFAFA]"}`}
                      data-testid={`ti-intake-customer-match-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[13.5px] truncate">{c.name}</div>
                        <div className="text-[11.5px] text-[#9CA3AF] truncate">{c.phone || "—"} · {c.email || "no email"}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {Number(c.store_credit_balance || 0) > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-[#FFFBEB] text-[#92400E] text-[10px] font-semibold">
                            ${Number(c.store_credit_balance).toFixed(2)} credit
                          </span>
                        )}
                        {c.licence_number ? (
                          <span className="text-[10px] text-[#1F6B4F] font-semibold uppercase tracking-wider">Licence ✓</span>
                        ) : (
                          <span className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider">No licence</span>
                        )}
                        <CaretRight size={12} className="text-[#9CA3AF]" />
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); startNew(); }}
                  onMouseEnter={() => setSearchHighlight(matches.length)}
                  className={`w-full text-left px-4 py-3 border-t border-[#F3F4F6] flex items-center gap-2.5 ${searchHighlight === matches.length ? "bg-[#EFF6FF]" : "bg-[#FAFAFA] hover:bg-[#EFF6FF]"} text-[#1E5FEB]`}
                  data-testid="ti-intake-customer-add-new"
                >
                  <UserPlus size={15} weight="bold" />
                  <span className="font-semibold text-[13px]">Add new customer</span>
                </button>
              </div>
            </div>
          )}

          {/* Existing customer card with inline compliance edits */}
          {customer && !creatingNew && (
            <div className="mt-3 border border-[#1E5FEB]/30 bg-[#EFF6FF]/40 rounded-xl p-4 space-y-3" data-testid="ti-intake-customer-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full bg-[#1F6B4F] text-white text-[10px] uppercase tracking-wider font-semibold">Selected</span>
                  <div className="font-semibold text-[14.5px]">{customer.name}</div>
                  <div className="text-[12.5px] text-[#52525B]">·  {customer.phone || "no phone"}</div>
                </div>
                <button onClick={() => { setCustomer(null); setCustomerSearch(""); }} className="text-[11px] text-[#9CA3AF] hover:text-[#B91C1C] font-medium">Change</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <BigInput icon={<IdentificationCard size={14} weight="bold" />} label="Licence #" value={customerEdits.licence_number} onChange={(v) => setCustomerEdits((s) => ({ ...s, licence_number: v }))} testid="ti-intake-edit-licence" />
                <BigSelect label="State" value={customerEdits.licence_state} onChange={(v) => setCustomerEdits((s) => ({ ...s, licence_state: v }))} options={STATES_AU} testid="ti-intake-edit-state" />
                <div className="sm:col-span-1"></div>
              </div>
              <div>
                <Label icon={<MapPin size={14} weight="bold" />}>Address</Label>
                <AddressAutocomplete
                  value={customerEdits.address}
                  onChange={(v) => setCustomerEdits((s) => ({ ...s, address: v }))}
                  testid="ti-intake-edit-address"
                />
              </div>
              <div className="text-[10.5px] text-[#9CA3AF]">Edits saved when you press <span className="font-medium">Save trade-in</span> below.</div>
            </div>
          )}

          {creatingNew && (
            <div className="mt-3 border border-[#E5E7EB] rounded-xl p-4 space-y-3" data-testid="ti-intake-new-customer">
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-[14px]">New customer</div>
                <button onClick={() => setCreatingNew(false)} className="text-[11px] text-[#9CA3AF] hover:text-[#121216] font-medium">Cancel</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <BigInput label="Full name *" value={newCustomer.name} onChange={(v) => setNewCustomer((s) => ({ ...s, name: v }))} testid="ti-intake-new-name" />
                <div>
                  <Label icon={<Phone size={14} weight="bold" />}>Phone *</Label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) => { setNewCustomer((s) => ({ ...s, phone: e.target.value })); setPhoneError(""); }}
                    onBlur={(e) => {
                      const formatted = formatAuPhone(e.target.value);
                      setNewCustomer((s) => ({ ...s, phone: formatted }));
                      validatePhoneOnBlur(e.target.value);
                    }}
                    placeholder="0400 000 000"
                    className={`h-12 w-full px-4 rounded-xl border-2 text-[14px] focus:outline-none transition-colors ${phoneError ? "border-[#DC2626] focus:border-[#DC2626]" : "border-[#E5E7EB] focus:border-[#1E5FEB]"}`}
                    data-testid="ti-intake-new-phone"
                  />
                  {phoneError && <div className="text-[11px] text-[#DC2626] mt-1" data-testid="ti-intake-new-phone-error">{phoneError}</div>}
                </div>
              </div>
              <BigInput label="Email" value={newCustomer.email} onChange={(v) => setNewCustomer((s) => ({ ...s, email: v }))} />
              <div className="grid grid-cols-3 gap-2">
                <BigInput icon={<IdentificationCard size={14} weight="bold" />} label="Licence # *" value={newCustomer.licence_number} onChange={(v) => setNewCustomer((s) => ({ ...s, licence_number: v }))} testid="ti-intake-new-licence" />
                <BigSelect label="State" value={newCustomer.licence_state} onChange={(v) => setNewCustomer((s) => ({ ...s, licence_state: v }))} options={STATES_AU} />
              </div>
              <div>
                <Label icon={<MapPin size={14} weight="bold" />}>Address *</Label>
                <AddressAutocomplete
                  value={newCustomer.address}
                  onChange={(v) => setNewCustomer((s) => ({ ...s, address: v }))}
                  testid="ti-intake-new-address"
                />
              </div>
              <div className="flex justify-end pt-1">
                <button
                  onClick={createNewCustomer}
                  disabled={!isValidAuPhone(newCustomer.phone)}
                  className="h-10 px-4 rounded-lg bg-[#121216] text-white text-[13px] font-semibold hover:bg-[#1F2937] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  data-testid="ti-intake-new-save"
                >
                  <UserPlus size={14} weight="bold" /> Save customer
                </button>
              </div>
            </div>
          )}
        </Section>

        {/* ── 2. DEVICES (repeatable rows) ─────────────────────────── */}
        <Section
          icon={<DeviceMobile size={16} weight="bold" />}
          title={`Devices (${devices.length})`}
          subtitle="Add every device the customer is selling. Each gets its own trade-in number."
        >
          <div className="space-y-3">
            {devices.map((d, idx) => {
              const bp = parseFloat(d.buy_price) || 0;
              const sp = parseFloat(d.sell_price) || 0;
              const margin = sp - bp;
              return (
                <div
                  key={idx}
                  className="rounded-xl border-2 border-[#E5E7EB] bg-white p-3"
                  data-testid={`ti-intake-device-row-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded bg-[#EFF6FF] text-[#1E5FEB] flex items-center justify-center text-[11px] font-black">
                      {idx + 1}
                    </div>
                    <div className="text-[12px] font-semibold text-[#4B5563]">Device #{idx + 1}</div>
                    {devices.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDevice(idx)}
                        className="ml-auto text-[#9CA3AF] hover:text-[#DC2626] p-1"
                        title="Remove this device"
                        data-testid={`ti-intake-device-remove-${idx}`}
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    )}
                  </div>
                  <TradeInDeviceFields value={d} onChange={(p) => updateDevice(idx, p)} idPrefix={`ti-intake-${idx}`} />
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <BigInput label="IMEI / Serial" value={d.imei}      onChange={(v) => updateDevice(idx, { imei: v })}         placeholder="353123456789012" testid={`ti-intake-imei-${idx}`} />
                    </div>
                    <BigInput label="Battery health %" value={d.battery_health} onChange={(v) => updateDevice(idx, { battery_health: clampPct(v) })} placeholder="e.g. 87 (optional)" testid={`ti-intake-battery-${idx}`} />
                    <Money    label="Buy price *"  value={d.buy_price}  onChange={(v) => updateDevice(idx, { buy_price: v })}  testid={`ti-intake-buy-price-${idx}`} />
                    <Money    label="Sell price *" value={d.sell_price} onChange={(v) => updateDevice(idx, { sell_price: v })} testid={`ti-intake-sell-price-${idx}`} />
                    <div className="col-span-2">
                      <BigInput label="Notes" value={d.notes} onChange={(v) => updateDevice(idx, { notes: v })} placeholder="FaceID disabled, small scratch, etc." />
                    </div>
                  </div>
                  {(bp > 0 || sp > 0) && (
                    <div className="mt-2 text-[11px] text-[#6B7280] font-mono">
                      Margin:{" "}
                      <span className={margin >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                        ${margin.toFixed(2)}
                      </span>
                      {bp > 0 && sp > bp && ` (${(((sp - bp) / bp) * 100).toFixed(0)}%)`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addDevice}
            disabled={devices.length >= 25}
            className="mt-3 w-full h-11 border-2 border-dashed border-[#E5E7EB] hover:border-[#1E5FEB] hover:bg-[#EFF6FF] text-[13px] font-semibold text-[#6B7280] hover:text-[#1E5FEB] flex items-center justify-center gap-2 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ti-intake-add-device"
          >
            <Plus size={14} weight="bold" />
            Add another device
            <span className="text-[11px] font-mono text-[#9CA3AF]">
              ({devices.length}/25)
            </span>
          </button>
        </Section>

        {/* ── 3. PAYOUT METHOD (shared) ────────────────────────────── */}
        <Section
          icon={<CurrencyCircleDollar size={16} weight="bold" />}
          title="Payout"
          subtitle="How the customer gets paid for ALL devices."
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { v: "cash", label: "Cash from till", sub: "Cash-out on active shift" },
              { v: "store_credit", label: "Store credit", sub: "Added to customer balance" },
              { v: "bank_transfer", label: "Bank transfer", sub: "Paid to their bank account" },
              { v: "payid", label: "PayID", sub: "Paid to their PayID" },
            ].map((opt) => {
              const active = payoutMethod === opt.v;
              return (
                <button
                  key={opt.v} type="button"
                  onClick={() => setPayoutMethod(opt.v)}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${active ? "border-[#1E5FEB] bg-[#EFF6FF]" : "border-[#E5E7EB] bg-white hover:border-[#1E5FEB]/40"}`}
                  data-testid={`ti-intake-payout-${opt.v}`}
                >
                  <div className="text-[13px] font-semibold">{opt.label}</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">{opt.sub}</div>
                </button>
              );
            })}
          </div>
          {(payoutMethod === "bank_transfer" || payoutMethod === "payid") && (
            <div className="mt-3">
              <label className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5563]">
                {TRADE_IN_PAYOUT_LABELS[payoutMethod]} reference <span className="text-[#DC2626]">*</span>
              </label>
              <input
                value={payoutReference}
                onChange={(e) => setPayoutReference(e.target.value)}
                placeholder={payoutMethod === "payid" ? "e.g. PayID receipt no. or 0412 345 678" : "e.g. bank receipt / transaction ID"}
                maxLength={120}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-[13px]"
                data-testid="ti-intake-payout-reference"
              />
              <div className="text-[11px] text-[#6B7280] mt-1">No cash leaves the till. Saved on the trade-in and printed on the receipt.</div>
            </div>
          )}
        </Section>

        {/* ── 4. LICENCE PHOTO (optional, shared) ─────────────────── */}
        <Section icon={<Camera size={16} weight="bold" />} title="Licence photo (optional)" subtitle="Useful for compliance audits. Max 1MB.">
          <input type="file" accept="image/*" onChange={(e) => onPhotoFile(e.target.files?.[0])} className="text-[12px]" data-testid="ti-intake-photo-input" />
          {licencePhotoUrl && (
            <div className="mt-2 flex items-start gap-3">
              <img src={licencePhotoUrl} alt="Licence" className="w-32 h-20 object-cover rounded border border-[#E5E7EB]" />
              <div className="flex-1">
                <div className="text-[11px] text-[#6B7280]">{Math.round(dataUrlSize(licencePhotoUrl) / 1024)} KB</div>
                <button onClick={() => setLicencePhotoUrl("")} className="text-[11px] text-[#B91C1C] hover:underline mt-1">Remove</button>
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Sticky totals + submit — always visible so cashier sees the
          payout total while adding devices. */}
      <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFA] grid grid-cols-[1fr_auto_auto] gap-3 items-center">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B7280] font-semibold">
              TOTAL PAYOUT
            </div>
            <div className="font-display font-bold text-[22px] tabular-nums leading-none mt-0.5 text-[#DC2626]" data-testid="ti-intake-total-payout">
              ${totalPayout.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B7280] font-semibold">
              TOTAL RESALE
            </div>
            <div className="font-display font-bold text-[22px] tabular-nums leading-none mt-0.5 text-emerald-600" data-testid="ti-intake-total-resale">
              ${totalResale.toFixed(2)}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="h-10 px-4 rounded-lg border border-[#E5E7EB] text-[13px] font-medium hover:bg-white" data-testid="ti-intake-cancel">Cancel</button>
        <button onClick={submit} disabled={submitting || !customer} className="h-10 px-5 rounded-lg bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5" data-testid="ti-intake-submit">
          {submitting ? "Saving…" : `Buy ${devices.length} device${devices.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </Wrapper>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────
function Wrapper({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ti-intake-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
function Header({ title, subtitle, onClose }) {
  return (
    <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
      <div>
        <div className="font-display font-semibold text-[18px]">{title}</div>
        {subtitle && <div className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</div>}
      </div>
      <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#121216]"><X size={16} weight="bold" /></button>
    </div>
  );
}
function Section({ icon, title, subtitle, children }) {
  return (
    <div>
      <div className="flex items-start gap-2 mb-2">
        <div className="w-7 h-7 mt-0.5 rounded-lg bg-[#EFF6FF] text-[#1E5FEB] flex items-center justify-center">{icon}</div>
        <div>
          <div className="font-semibold text-[14px]">{title}</div>
          <div className="text-[11.5px] text-[#6B7280]">{subtitle}</div>
        </div>
      </div>
      <div className="pl-9">{children}</div>
    </div>
  );
}
function Label({ children, icon }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1 flex items-center gap-1.5">
      {icon}
      {children}
    </div>
  );
}
function BigInput({ label, value, onChange, placeholder = "", testid, icon }) {
  return (
    <div>
      <Label icon={icon}>{label}</Label>
      <input
        value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="h-12 w-full px-4 rounded-xl border-2 border-[#E5E7EB] text-[14px] focus:border-[#1E5FEB] outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
function Money({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-[14px]">$</span>
        <input
          type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
          className="h-12 w-full pl-8 pr-4 rounded-xl border-2 border-[#E5E7EB] text-[14px] focus:border-[#1E5FEB] outline-none tabular-nums"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
function BigSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-12 w-full px-4 rounded-xl border-2 border-[#E5E7EB] text-[14px] bg-white focus:border-[#1E5FEB] outline-none" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-[14px] font-semibold mt-0.5">{value}</div>
    </div>
  );
}
